import { create } from '@bufbuild/protobuf';

import { WsRequest } from 'APP/wsApi/wsAppApi/requests/request.types';
import {
  CalendarAccount,
  CalendarAccountSchema,
  CalendarAction,
  CalendarEvent,
  CalendarType,
  type SubscribeToCalendarPush,
  SubscribeToCalendarPushSchema,
} from 'APP/wsApi/wsAppApi/types/calendar/event_model_pb';
import {
  SubscribeToCalendarPushRequest,
  CreateCalendarEventRequest,
  DeleteCalendarEventRequest,
  DeleteRecurrenceEventRequest,
  LogoutCalendarRequest,
  RequestReminderSchema,
  UpdateCalendarEventRequest,
  UpdateEventParticipantStatusRequest,
  UpdateRecurrenceCalendarEventRequest,
  GetCalendarAuthUrlRequest,
} from 'APP/wsApi/wsAppApi/types/calendar/event_request_pb';
import { Platform } from 'APP/wsApi/wsAppApi/types/common/common_model_pb';

import {
  CalendarRealtimeAction,
  ICalendarAccount,
  ICreateCalendarEventParams,
  IDeleteCalendarEventParams,
  IDeleteRecurrenceEventParams,
  IEvent,
  IGetCalendarAuthUrlParams,
  ILogoutCalendarParams,
  IUpdateCalendarEventParams,
  IUpdateEventParticipantStatusParams,
  IUpdateRecurrenceCalendarEventParams,
} from './calendar.types';

export function mapCalendarEventResponse(event: CalendarEvent): IEvent {
  return {
    id: event.providerEventId,
    providerEventId: event.providerEventId,
    parentId: event.parentId,
    account: {
      providerEmail: event.account?.providerEmail || '',
      type: event.account?.type,
    },
    title: event.title,
    start: new Date(Number(event.startTimeTs)),
    end: new Date(Number(event.endTimeTs)),
    initiatorEmail: event.initiatorEmail,
    allDay: event.allDay,
    timeZone: event.timeZone,
    location: event.location,
    description: event.description,
    recurrenceType: event.recurrenceType,
    chatLink: event.chatLink,
    callLink: event.callLink,
    reminders: event.reminders.map((reminder) => ({
      type: reminder.type,
      time: Number(reminder.time),
      unit: reminder.unit,
    })),
    participants: event.participants.map((participant) => ({
      email: participant.email,
      confirmationType: participant.confirmationType,
    })),
  };
}

export function mapCalendarEvents(events: CalendarEvent[]): IEvent[] {
  return events.map(mapCalendarEventResponse);
}

export function mapCalendarAccountRequest(account: ICalendarAccount): CalendarAccount {
  return create(CalendarAccountSchema, {
    providerEmail: account.providerEmail,
    type: CalendarType.GOOGLE,
  });
}

export function mapCalendarAccountsRequest(accounts: ICalendarAccount[]): CalendarAccount[] {
  return accounts.map(mapCalendarAccountRequest);
}

export function mapSubscribeToCalendarPush(account: ICalendarAccount): SubscribeToCalendarPush {
  return create(SubscribeToCalendarPushSchema, {
    account: mapCalendarAccountRequest(account),
  });
}

export function mapSubscribeToCalendarPushRequest(
  accounts: ICalendarAccount[]
): WsRequest<SubscribeToCalendarPushRequest> {
  return {
    subscribes: accounts.map(mapSubscribeToCalendarPush),
    platform: Platform.WEB,
  };
}

export function mapCalendarRealtimeEventAction(action: CalendarAction): CalendarRealtimeAction {
  switch (action) {
    case CalendarAction.UPDATE:
      return CalendarRealtimeAction.UPDATE;
    case CalendarAction.DELETE:
      return CalendarRealtimeAction.DELETE;
    case CalendarAction.CREATE:
      return CalendarRealtimeAction.CREATE;
    default:
      return CalendarRealtimeAction.UNKNOWN;
  }
}

export function mapDeleteCalendarEventParams(
  params: IDeleteCalendarEventParams
): WsRequest<DeleteCalendarEventRequest> {
  return {
    ...params,
    account: create(CalendarAccountSchema, params.account),
  };
}

export function mapDeleteRecurrenceEventParams(
  params: IDeleteRecurrenceEventParams
): WsRequest<DeleteRecurrenceEventRequest> {
  return {
    ...params,
    account: create(CalendarAccountSchema, params.account),
  };
}

export function mapCreateCalendarEventParams(
  params: ICreateCalendarEventParams
): WsRequest<CreateCalendarEventRequest> {
  return {
    ...params,
    reminders: params.reminders.map((reminder) => create(RequestReminderSchema, reminder)),
    account: create(CalendarAccountSchema, params.account),
  };
}

export function mapUpdateCalendarEventParams(
  params: IUpdateCalendarEventParams
): WsRequest<UpdateCalendarEventRequest> {
  return {
    ...params,
    reminders: params.reminders.map((reminder) => create(RequestReminderSchema, reminder)),
    account: create(CalendarAccountSchema, params.account),
  };
}

export function mapUpdateRecurrenceCalendarEventParams(
  params: IUpdateRecurrenceCalendarEventParams
): WsRequest<UpdateRecurrenceCalendarEventRequest> {
  return {
    ...params,
    reminders: params.reminders.map((reminder) => create(RequestReminderSchema, reminder)),
    account: create(CalendarAccountSchema, params.account),
  };
}

export function mapUpdateEventParticipantStatusParams(
  params: IUpdateEventParticipantStatusParams
): WsRequest<UpdateEventParticipantStatusRequest> {
  return {
    ...params,
    account: create(CalendarAccountSchema, params.account),
  };
}

export function mapLogoutCalendarParams(
  params: ILogoutCalendarParams
): WsRequest<LogoutCalendarRequest> {
  return {
    ...params,
    account: create(CalendarAccountSchema, params.account),
  };
}

export function mapGetCalendarAuthUrlParams(
  params: IGetCalendarAuthUrlParams
): WsRequest<GetCalendarAuthUrlRequest> {
  return {
    ...params,
    platform: Platform.WEB,
  };
}
